import { create, type StateCreator } from "zustand";
import { persist } from "zustand/middleware";

import type { Product } from "@/features/products/types/product.types";
import { nowUtcIso } from "@/utils";

export interface SaleLineItem {
  productId: string;
  name: string;
  externalId: string | null;
  brand: string;
  unitPrice: number;
  quantity: number;
  stockOnHand: number;
}

interface CheckoutCartState {
  saleItems: Record<string, SaleLineItem>;
  startedAtIso: string;
}

interface CheckoutCartActions {
  addProduct: (product: Product) => void;
  setProductQuantity: (product: Product, nextQuantity: number) => void;
  setLineItemQuantity: (productId: string, nextQuantity: number) => void;
  removeItem: (productId: string) => void;
  clearSale: () => void;
}

type CheckoutCartStore = CheckoutCartState & CheckoutCartActions;

function createInitialState(): CheckoutCartState {
  return {
    saleItems: {},
    startedAtIso: nowUtcIso(),
  };
}

function normalizeQuantity(quantity: number) {
  if (!Number.isFinite(quantity)) {
    return 0;
  }

  return Math.max(0, Math.trunc(Math.abs(quantity)));
}

const storeCreator: StateCreator<CheckoutCartStore> = (set) => ({
  ...createInitialState(),

  addProduct: (product) => {
    set((state) => {
      const currentLine = state.saleItems[product.id];
      const isStartingSale = Object.keys(state.saleItems).length === 0;
      const maxQuantity = product.stockOnHand ?? 0;
      const newQuantity = currentLine
        ? Math.min(currentLine.quantity + 1, maxQuantity)
        : Math.min(1, maxQuantity);

      if (newQuantity === 0) {
        return state;
      }

      return {
        startedAtIso: isStartingSale
          ? nowUtcIso()
          : state.startedAtIso,
        saleItems: {
          ...state.saleItems,
          [product.id]: {
            productId: product.id,
            name: product.name,
            externalId: product.externalId,
            brand: product.brand,
            unitPrice: product.currentPrice,
            quantity: newQuantity,
            stockOnHand: maxQuantity,
          },
        },
      };
    });
  },

  setProductQuantity: (product, nextQuantity) => {
    const normalizedQuantity = normalizeQuantity(nextQuantity);
    const maxQuantity = product.stockOnHand ?? 0;
    const cappedQuantity = Math.min(normalizedQuantity, maxQuantity);

    set((state) => {
      if (cappedQuantity === 0) {
        const nextItems = { ...state.saleItems };
        delete nextItems[product.id];
        return { saleItems: nextItems };
      }

      const isStartingSale = Object.keys(state.saleItems).length === 0;

      return {
        startedAtIso: isStartingSale
          ? nowUtcIso()
          : state.startedAtIso,
        saleItems: {
          ...state.saleItems,
          [product.id]: {
            productId: product.id,
            name: product.name,
            externalId: product.externalId,
            brand: product.brand,
            unitPrice: product.currentPrice,
            quantity: cappedQuantity,
            stockOnHand: maxQuantity,
          },
        },
      };
    });
  },

  setLineItemQuantity: (productId, nextQuantity) => {
    const normalizedQuantity = normalizeQuantity(nextQuantity);

    set((state) => {
      if (!(productId in state.saleItems)) {
        return state;
      }

      const maxQuantity = state.saleItems[productId].stockOnHand ?? 0;
      const cappedQuantity = Math.min(normalizedQuantity, maxQuantity);

      if (cappedQuantity === 0) {
        const nextItems = { ...state.saleItems };
        delete nextItems[productId];
        return { saleItems: nextItems };
      }

      return {
        saleItems: {
          ...state.saleItems,
          [productId]: {
            ...state.saleItems[productId],
            quantity: cappedQuantity,
          },
        },
      };
    });
  },

  removeItem: (productId) => {
    set((state) => {
      const nextItems = { ...state.saleItems };
      delete nextItems[productId];
      return { saleItems: nextItems };
    });
  },

  clearSale: () => set(createInitialState()),
});

export const useCheckoutCartStore = create<CheckoutCartStore>()(
  persist(storeCreator, {
    name: "mi-alegria-checkout-cart",
    partialize: (state: CheckoutCartStore) => ({
      saleItems: state.saleItems,
      startedAtIso: state.startedAtIso,
    }),
  }),
);
